/**
 * One screen for the five field sections:
 *  - add-only (N-PAC, 28 Oct 2026): the server sends counts, never what is already saved; the form
 *    opens with the unfinished draft, else the unsent outbox copy (the surveyor's own entries), else
 *    empty, and parts the server already has show "✓ already filled". The server adds what is sent.
 *  - every change is kept as a draft in the local database (debounced, and immediately when the
 *    page is hidden or closed), so nothing typed is lost
 *  - Save = validate with @npac/rules → local database + outbox → try to send now
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { countEntries, type SectionName } from "@npac/rules";
import type { FormBase } from "@core/app";
import { outboxErrorText } from "@core/messages";
import { AGENT_KEYS, dataToForm, emptyForm, serverErrorsToFieldErrors, validateForm, type AnyForm, type FieldError, type FormBySection } from "@core/forms";
import type { FieldSection } from "@core/types";
import { formatTime, localizeError } from "../i18n";
import { useApp, useSession } from "../ui/context";
import { navigate } from "../ui/router";
import { Banner, Button } from "../ui/components";
import { AgentsFields, DemographicsFields, Filled, GroundFields, IdentityFields, KaryakartaFields } from "./SectionFields";

type Loaded = { form: AnyForm; base: FormBase; source: "draft" | "outbox" | "empty"; draftAt?: number; have: Record<string, number> };

const DRAFT_DEBOUNCE_MS = 600;

export function SectionScreen({ code, section }: { code: string; section: FieldSection }) {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [form, setFormState] = useState<AnyForm | null>(null);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [banner, setBanner] = useState<{ kind: "ok" | "warn" | "error" | "info"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [editable, setEditable] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [rejected, setRejected] = useState<string[] | null>(null);

  const dirty = useRef(false);
  const saved = useRef(false);
  const formRef = useRef<AnyForm | null>(null);
  const baseRef = useRef<FormBase>({ baseVersion: 0, basedOnItemId: null });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!assemblyId) return;
    let cancelled = false;
    (async () => {
      const sec = section as SectionName;
      const [booth, draft, item, server] = await Promise.all([
        app.core.store.getBooth(assemblyId, code),
        app.core.store.getDraft<AnyForm>(assemblyId, code, sec),
        app.core.store.findSectionItem(assemblyId, code, sec),
        app.core.store.getServerSection(assemblyId, code, sec),
      ]);
      if (cancelled) return;
      setEditable(!!booth && app.canEdit(booth));
      setConflict(item?.status === "conflict");
      if (item?.status === "failed" && item.lastError) {
        const d = item.lastError.details ?? [];
        setRejected(d.length ? d.map((x) => `${x.path}: ${x.message}`) : [outboxErrorText(item.lastError, lang)]);
      }
      const serverVersion = server?.version ?? booth?.sections?.[sec]?.version ?? 0;
      const have = server?.counts ?? {};
      let l: Loaded;
      if (draft) l = { form: draft.form, base: { baseVersion: draft.baseVersion, basedOnItemId: draft.basedOnItemId ?? null }, source: "draft", draftAt: draft.savedAt, have };
      else if (item) l = { form: dataToForm(section, item.payload.data), base: { baseVersion: item.baseVersion, basedOnItemId: item.id }, source: "outbox", have };
      else l = { form: emptyForm(section), base: { baseVersion: serverVersion, basedOnItemId: null }, source: "empty", have };
      baseRef.current = l.base;
      formRef.current = l.form;
      setLoaded(l);
      setFormState(l.form);
      if (l.source === "draft") setBanner({ kind: "info", text: `${t.draftRestored} (${formatTime(l.draftAt ?? null, lang)})` });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, assemblyId, code, section]);

  const flushDraft = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!dirty.current || saved.current || !formRef.current || !assemblyId) return;
    dirty.current = false;
    await app.core.store
      .saveDraft(assemblyId, code, section as SectionName, formRef.current, baseRef.current.baseVersion, baseRef.current.basedOnItemId)
      .catch(() => {
        dirty.current = true;
      });
  }, [app, assemblyId, code, section]);

  const setForm = useCallback(
    (f: AnyForm) => {
      formRef.current = f;
      setFormState(f);
      dirty.current = true;
      saved.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flushDraft(), DRAFT_DEBOUNCE_MS);
    },
    [flushDraft]
  );

  // page hidden (app switched, screen off) or closed → write the draft now; leaving the screen → same
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void flushDraft();
    };
    const onPageHide = () => void flushDraft();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      void flushDraft();
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [flushDraft]);

  const save = async () => {
    if (!formRef.current) return;
    const pre = validateForm(section, formRef.current);
    if (pre.ok && countEntries(section as SectionName, pre.data) === 0) {
      setBanner({ kind: "warn", text: t.aoNothingNew });
      return;
    }
    setSaving(true);
    setBanner(null);
    try {
      if (timer.current) clearTimeout(timer.current);
      dirty.current = false;
      const r = await app.core.saveSection(code, section, formRef.current, baseRef.current);
      if (!r.ok) {
        setErrors(r.errors);
        setBanner({ kind: "error", text: t.formHasErrors });
        dirty.current = true;
        await flushDraft();
        return;
      }
      saved.current = true;
      baseRef.current = { baseVersion: baseRef.current.baseVersion, basedOnItemId: r.itemId };
      setErrors([]);
      setRejected(null);
      const item = assemblyId ? await app.core.store.findSectionItem(assemblyId, code, section as SectionName) : null;
      if (item?.status === "failed" && item.lastError) {
        const d = item.lastError.details ?? [];
        if (d.length) setErrors(serverErrorsToFieldErrors(section, formRef.current, d));
        setRejected(d.length ? d.map((x) => `${x.path}: ${x.message}`) : [outboxErrorText(item.lastError, lang)]);
        setBanner({ kind: "error", text: t.serverRejected });
        return;
      }
      if (item?.status === "conflict") {
        setConflict(true);
        setBanner({ kind: "warn", text: t.pendingConflictBanner });
        return;
      }
      // sent: the server says what it added and what it already had (it never overwrites)
      const srv = r.synced && assemblyId ? await app.core.store.getServerSection(assemblyId, code, section as SectionName) : null;
      const added = srv?.added != null ? t.aoAdded(srv.added, srv.skipped ?? 0) : null;
      setBanner({
        kind: r.status === "mismatch" ? "warn" : "ok",
        text: r.status === "mismatch" ? t.formMismatchSaved : added ?? (r.synced ? t.formSavedSynced : t.formSavedQueued),
      });
      closeTimer.current = setTimeout(() => navigate({ name: "booth", code }), r.status === "mismatch" || srv?.skipped ? 2200 : 1100);
    } catch (e) {
      setBanner({ kind: "error", text: `${t.err_generic}: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setSaving(false);
    }
  };

  const discardDraft = async () => {
    if (!assemblyId || !window.confirm(t.discardDraftConfirm)) return;
    if (timer.current) clearTimeout(timer.current);
    dirty.current = false;
    saved.current = true;
    await app.core.store.deleteDraft(assemblyId, code, section as SectionName);
    navigate({ name: "booth", code });
  };

  if (!loaded || !form) return null;
  const errMap: Record<string, string> = {};
  for (const e of errors) if (!errMap[e.fieldKey]) errMap[e.fieldKey] = localizeError(e.message, lang);
  const have = loaded.have;
  // nothing left to add from the phone: every part is filled (corrections are on the web)
  const full =
    loaded.source === "empty" &&
    ((section === "identity" && ["mandal", "panchayat", "village", "landmark"].every((k) => have[k])) ||
      (section === "agents" && AGENT_KEYS.every((k) => have[k])) ||
      (section === "karyakarta" && (have.people ?? 0) >= 20) ||
      (section === "demographics" && have.totalVoters) ||
      (section === "ground" && (have.problems ?? 0) >= 10 && (have.suggestions ?? 0) >= 10));
  const canAdd = editable && !full;
  const common = { errors: errMap, editable: canAdd, have };
  const tableErrors = errors.filter((e) => !e.fieldKey.includes(".") && !["totalVoters", "otherGender", "mandal", "panchayat", "village", "landmark"].includes(e.fieldKey));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid={`form-${section}`}
    >
      <h2>
        {t.boothNo} {code} — {t[`sec_${section}` as const]}
      </h2>
      {!editable ? (
        <Banner kind="warn" testId="form-not-assigned">
          {t.notAllowedEdit}
        </Banner>
      ) : null}
      {conflict ? <Banner kind="warn">{t.pendingConflictBanner}</Banner> : null}
      {banner ? (
        <Banner kind={banner.kind} testId="form-banner">
          {banner.text}
        </Banner>
      ) : null}
      {rejected ? (
        <Banner kind="error">
          {rejected.map((m, i) => (
            <div key={i}>• {m}</div>
          ))}
        </Banner>
      ) : null}
      {tableErrors.map((e, i) => (
        <Banner key={i} kind="error">{`${e.path || "—"}: ${localizeError(e.message, lang)}`}</Banner>
      ))}
      <p className="muted ao-note">{t.aoNote}</p>

      {section === "demographics" && have.totalVoters ? (
        <div className="stack" data-testid="demo-locked">
          <Filled label={t.totalVoters} />
          {have.age ? <Filled label={t.ageGroups} /> : null}
          <div className="ao-filled">
            <span>{t.casteTable}</span>
            <b>{t.aoCastes(have.castes ?? 0)}</b>
          </div>
          <Banner kind="info">{t.aoDemoLocked}</Banner>
        </div>
      ) : (
      <fieldset disabled={!canAdd} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {section === "identity" ? <IdentityFields {...common} form={form as FormBySection["identity"]} setForm={setForm} /> : null}
        {section === "agents" ? <AgentsFields {...common} form={form as FormBySection["agents"]} setForm={setForm} /> : null}
        {section === "karyakarta" ? <KaryakartaFields {...common} form={form as FormBySection["karyakarta"]} setForm={setForm} /> : null}
        {section === "demographics" ? <DemographicsFields {...common} form={form as FormBySection["demographics"]} setForm={setForm} /> : null}
        {section === "ground" ? <GroundFields {...common} form={form as FormBySection["ground"]} setForm={setForm} /> : null}
      </fieldset>
      )}

      {canAdd ? (
        <div className="stack" style={{ marginTop: 16 }}>
          <Button type="submit" big busy={saving} testId="form-save">
            ✓ {t.save}
          </Button>
          {loaded.source === "draft" ? (
            <Button variant="danger" onClick={discardDraft}>
              {t.discardDraft}
            </Button>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
